import { z } from 'zod';
const money = z.number().int().min(0).max(1_000_000_000);
export const pricingCost = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(120),
  amountCents: money,
  allocationMonths: z.number().int().min(1).max(120),
  isSubscription: z.boolean().default(false),
});
export const pricingSettingsInput = z
  .object({
    costs: z.array(pricingCost).max(200),
    remunerationCents: money,
    productiveHours: z.number().min(1).max(744),
    marginBps: z.number().int().min(0).max(9500),
    taxBps: z.number().int().min(0).max(9500),
  })
  .refine((v) => v.marginBps + v.taxBps < 10000, {
    message: 'Margem e impostos precisam somar menos de 100%.',
    path: ['marginBps'],
  });
export type PricingSettings = z.infer<typeof pricingSettingsInput>;
export const pricingRecipeInput = z.object({
  mode: z.enum(['volume', 'job']).default('volume'),
  minutes: z.number().int().min(1).max(100000),
  directCostCents: money,
  marginBps: z.number().int().min(0).max(9500).nullable(),
});
export type PricingRecipe = z.infer<typeof pricingRecipeInput>;
export const pricingEstimateDto = z.object({
  monthlyCostCents: z.number(),
  hourlyCostCents: z.number(),
  laborCostCents: z.number(),
  directCostCents: z.number(),
  subscriptionCostCents: z.number(),
  costCents: z.number(),
  minimumCents: z.number(),
  suggestedCents: z.number(),
  taxCents: z.number(),
  profitCents: z.number(),
});
export function calculatePricing(settings: PricingSettings, recipe: PricingRecipe) {
  const margin = recipe.marginBps ?? settings.marginBps;
  if (margin + settings.taxBps >= 10000) return null;
  const monthly =
    settings.costs.reduce((total, c) => total + c.amountCents / c.allocationMonths, 0) +
    settings.remunerationCents;
  const hour = monthly / settings.productiveHours;
  // A complex job covers a full month of subscriptions in addition to the hourly base.
  // Annual subscriptions contribute their monthly equivalent, once per job.
  const subscriptions =
    recipe.mode === 'job'
      ? settings.costs
          .filter((c) => c.isSubscription)
          .reduce((total, c) => total + c.amountCents / c.allocationMonths, 0)
      : 0;
  const cost = Math.ceil((hour * recipe.minutes) / 60 + recipe.directCostCents + subscriptions);
  const minimum = Math.ceil(cost / (1 - settings.taxBps / 10000));
  const suggested = Math.ceil(cost / (1 - (margin + settings.taxBps) / 10000));
  if (!Number.isSafeInteger(suggested) || suggested > 1e13) return null;
  const tax = Math.round((suggested * settings.taxBps) / 10000);
  return {
    monthlyCostCents: Math.ceil(monthly),
    hourlyCostCents: Math.ceil(hour),
    laborCostCents: Math.ceil((hour * recipe.minutes) / 60),
    directCostCents: recipe.directCostCents,
    subscriptionCostCents: Math.ceil(subscriptions),
    costCents: cost,
    minimumCents: minimum,
    suggestedCents: suggested,
    taxCents: tax,
    profitCents: suggested - cost - tax,
  };
}
export const pricingWorkspaceDto = z.object({
  settings: pricingSettingsInput.nullable(),
  materials: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      category: z.string(),
      priceCents: z.number().nullable(),
      recipe: pricingRecipeInput.nullable(),
      estimate: pricingEstimateDto.nullable(),
    }),
  ),
});
export type PricingWorkspace = z.infer<typeof pricingWorkspaceDto>;
